import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '../../App';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Activity, Search, RefreshCw, Users, MousePointer, 
  Eye, Filter, ChevronLeft, ChevronRight, TrendingUp,
  Calendar, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
});

// Action type badge colors
const getActionBadge = (actionType) => {
  const colors = {
    page_view: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    navigation: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    button_click: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    form_submit: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    api_call: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    login: 'bg-green-500/20 text-green-400 border-green-500/30',
    logout: 'bg-red-500/20 text-red-400 border-red-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return colors[actionType] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
};

// Format timestamp for display
const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-xl">
        <p className="text-zinc-400 text-xs mb-1">{label}</p>
        <p className="text-amber-400 font-semibold">
          {payload[0].value} active users
        </p>
        {payload[1] && (
          <p className="text-zinc-500 text-xs mt-1">
            {payload[1].value} total actions
          </p>
        )}
      </div>
    );
  }
  return null;
};

export default function SuperAdminUserLogs() {
  const { user } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedActionType, setSelectedActionType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [chartDays, setChartDays] = useState(30);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/platform/activity-stats?days=${chartDays}`,
        getAuthConfig()
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [chartDays]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25'
      });
      
      if (search) params.append('search', search);
      if (selectedActionType && selectedActionType !== 'all') {
        params.append('action_type', selectedActionType);
      }
      
      const response = await axios.get(
        `${API}/platform/activity-logs?${params}`,
        getAuthConfig()
      );
      
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedActionType]);

  const fetchActionTypes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/platform/activity-action-types`,
        getAuthConfig()
      );
      setActionTypes(response.data.action_types);
    } catch (error) {
      console.error('Failed to fetch action types:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchActionTypes();
  }, [fetchStats, fetchActionTypes]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
    toast.success('Data refreshed');
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-3">
              <Activity className="w-7 h-7 text-amber-500" />
              User Activity
            </h1>
            <p className="text-zinc-500 mt-1">
              Track user actions and engagement across the platform
            </p>
          </div>
          <Button 
            onClick={handleRefresh}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="btn-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Total Users</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">
                    {stats?.summary?.total_users || 0}
                  </p>
                </div>
                <Users className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Total Actions</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">
                    {stats?.summary?.total_activity_logs?.toLocaleString() || 0}
                  </p>
                </div>
                <MousePointer className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Today's Active</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">
                    {stats?.daily_stats?.slice(-1)[0]?.active_users || 0}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Avg Daily Active</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">
                    {stats?.daily_stats?.length > 0 
                      ? Math.round(stats.daily_stats.reduce((a, b) => a + b.active_users, 0) / stats.daily_stats.length)
                      : 0
                    }
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Users Chart */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-zinc-100 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  Daily Active Users
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Users who performed at least one action per day
                </CardDescription>
              </div>
              <Select value={chartDays.toString()} onValueChange={(v) => setChartDays(parseInt(v))}>
                <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {stats?.daily_stats?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daily_stats}>
                    <defs>
                      <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="active_users"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorUsers)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-500">
                  <div className="text-center">
                    <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No activity data yet</p>
                    <p className="text-sm">Activity will appear here as users interact with the platform</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Actions Card */}
        {stats?.top_actions?.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-zinc-100 text-sm">Top Action Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {stats.top_actions.map((action, i) => (
                  <Badge 
                    key={i}
                    className={`${getActionBadge(action.action)} px-3 py-1`}
                  >
                    {action.action}: {action.count.toLocaleString()}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity Logs Table */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-zinc-100 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-500" />
                  Activity Log
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  {total.toLocaleString()} total events
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder="Search by user, action, or path..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-zinc-800 border-zinc-700 text-zinc-100"
                    data-testid="input-search"
                  />
                </div>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-zinc-900">
                  Search
                </Button>
              </form>
              
              <Select 
                value={selectedActionType} 
                onValueChange={(v) => { setSelectedActionType(v); setPage(1); }}
              >
                <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-zinc-300">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="rounded-lg border border-zinc-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">User</TableHead>
                    <TableHead className="text-zinc-400">Action</TableHead>
                    <TableHead className="text-zinc-400">Detail</TableHead>
                    <TableHead className="text-zinc-400">Path</TableHead>
                    <TableHead className="text-zinc-400 text-right">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                      </TableCell>
                    </TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                        <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p>No activity logs found</p>
                        {search && <p className="text-sm">Try adjusting your search</p>}
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <div>
                            <p className="text-zinc-100 font-medium text-sm">
                              {log.user_name || 'Unknown'}
                            </p>
                            <p className="text-zinc-500 text-xs">{log.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getActionBadge(log.action_type)} text-xs`}>
                            {log.action_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm max-w-xs truncate">
                          {log.action_detail || '-'}
                        </TableCell>
                        <TableCell className="text-zinc-500 text-xs font-mono max-w-xs truncate">
                          {log.page_path || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 text-zinc-500 text-xs">
                            <Clock className="w-3 h-3" />
                            {formatTimestamp(log.created_at)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-zinc-500 text-sm">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
